import { NumberInput, Select, TextArea, TextInput } from '@la/ds-ui-components';
import { ProductProperty, PropertyValue, UserSelection } from '@la/types';
import { convertToKebabCase } from '@la/utilities';
import { CardDisplayType } from '../MemberRegistration.types';
import styles from './AddOnCard.module.scss';

export default function AddOnSelections({
  addOnQuantity,
  cardDisplayType,
  productId,
  properties,
  selectionsWithErrors,
  updateAddOnQuantity,
  updateAddOnSelection,
  userSelections,
}: Readonly<{
  addOnQuantity?: number;
  cardDisplayType: CardDisplayType;
  productId: number;
  properties?: ProductProperty[];
  selectionsWithErrors: string[];
  updateAddOnQuantity?: (quantity: number) => void;
  updateAddOnSelection: (
    addOnId: number,
    propertyId: string,
    value: string
  ) => void;
  userSelections: UserSelection[];
}>) {
  return (
    <div className={styles.selections}>
      {properties ? (
        <SelectionFields
          properties={properties}
          productId={productId}
          selectionsWithErrors={selectionsWithErrors}
          updateAddOnSelection={updateAddOnSelection}
          userSelections={userSelections}
        />
      ) : null}
      {cardDisplayType === 'optional' &&
      addOnQuantity &&
      updateAddOnQuantity ? (
        <AddOnQuantityPicker
          addOnQuantity={addOnQuantity}
          productId={productId}
          updateAddOnQuantity={updateAddOnQuantity}
        />
      ) : null}
    </div>
  );
}

function SelectionFields({
  properties,
  productId,
  selectionsWithErrors,
  updateAddOnSelection,
  userSelections,
}: Readonly<{
  properties: ProductProperty[];
  productId: number;
  selectionsWithErrors: string[];
  updateAddOnSelection: (
    addOnId: number,
    propertyId: string,
    value: string
  ) => void;
  userSelections: UserSelection[];
}>) {
  function renderSelectionFields(properties: ProductProperty[]) {
    return properties.map((property) => {
      const hasError = selectionsWithErrors.includes(property.id.toString());
      switch (property.inputControlType) {
        case 'NUMERIC':
          return (
            <AddOnNumericInput
              hasError={hasError}
              key={property.id}
              id={property.id}
              name={property.name}
              required={property.required}
              productId={productId}
              updateAddOnSelection={updateAddOnSelection}
              userSelections={userSelections}
            />
          );
        case 'PICK_LIST':
          return (
            <AddOnSelectMenu
              hasError={hasError}
              id={property.id}
              key={property.id}
              name={property.name}
              productId={productId}
              required={property.required}
              updateAddOnSelection={updateAddOnSelection}
              userSelections={userSelections}
              values={property.values ?? []}
            />
          );
        case 'TEXT_AREA':
          return (
            <AddOnTextArea
              hasError={hasError}
              id={property.id}
              key={property.id}
              name={property.name}
              productId={productId}
              required={property.required}
              updateAddOnSelection={updateAddOnSelection}
              userSelections={userSelections}
            />
          );
        case 'TEXT_BOX':
          return (
            <AddOnTextInput
              hasError={hasError}
              key={property.id}
              id={property.id}
              name={property.name}
              required={property.required}
              productId={productId}
              updateAddOnSelection={updateAddOnSelection}
              userSelections={userSelections}
            />
          );
        default:
          console.error(
            `Unsupported input control type: ${property.inputControlType}`
          );
          return null;
      }
    });
  }

  return <>{renderSelectionFields(properties)}</>;
}

function AddOnNumericInput({
  hasError,
  id,
  name,
  required,
  productId,
  updateAddOnSelection,
  userSelections,
}: Readonly<{
  hasError: boolean;
  id: number;
  name: string;
  required: boolean;
  productId: number;
  updateAddOnSelection: (
    addOnId: number,
    propertyId: string,
    value: string
  ) => void;
  userSelections: UserSelection[];
}>) {
  const currentValue =
    userSelections.find((selection) => selection.optionId === id.toString())
      ?.selectedValue || '';

  const handleChange = (value: string) => {
    updateAddOnSelection(productId, id.toString(), value);
  };

  return (
    <NumberInput
      errorMessage="Please enter a value"
      hasError={hasError}
      id={id.toString()}
      label={name}
      min={0}
      required={required}
      size="medium"
      value={currentValue}
      onChangeValue={(number) => handleChange(number.toString())}
    />
  );
}

function AddOnSelectMenu({
  hasError,
  id,
  name,
  values,
  required,
  productId,
  updateAddOnSelection,
  userSelections,
}: Readonly<{
  hasError: boolean;
  id: number;
  name: string;
  required: boolean;
  values: PropertyValue[];
  productId: number;
  updateAddOnSelection: (
    addOnId: number,
    propertyId: string,
    value: string
  ) => void;
  userSelections: UserSelection[];
}>) {
  const options = values.map((option: PropertyValue) => ({
    label: option.name,
    value: option.value,
  }));

  const currentValue =
    userSelections.find((selection) => selection.optionId === id.toString())
      ?.selectedValue || '';

  const handleChange = (value: string) => {
    updateAddOnSelection(productId, id.toString(), value);
  };

  return (
    <Select
      errorMessage="Please select an option"
      hasError={hasError}
      id={id.toString()}
      placeholder="Select an option"
      size="medium"
      label={name}
      name={convertToKebabCase(name)}
      required={required}
      options={options}
      value={currentValue}
      onChange={handleChange}
    ></Select>
  );
}

function AddOnTextArea({
  hasError,
  id,
  name,
  required,
  productId,
  updateAddOnSelection,
  userSelections,
}: Readonly<{
  hasError: boolean;
  id: number;
  name: string;
  required: boolean;
  productId: number;
  updateAddOnSelection: (
    addOnId: number,
    propertyId: string,
    value: string
  ) => void;
  userSelections: UserSelection[];
}>) {
  const currentValue =
    userSelections.find((selection) => selection.optionId === id.toString())
      ?.selectedValue || '';

  const handleChange = (value: string) => {
    updateAddOnSelection(productId, id.toString(), value);
  };

  return (
    <TextArea
      errorMessage="Please enter a value"
      hasError={hasError}
      id={id.toString()}
      label={name}
      maxLength={255}
      name={convertToKebabCase(name)}
      required={required}
      value={currentValue}
      onChange={(evt) => handleChange(evt.target.value)}
    />
  );
}

function AddOnTextInput({
  hasError,
  id,
  name,
  required,
  productId,
  updateAddOnSelection,
  userSelections,
}: Readonly<{
  hasError: boolean;
  id: number;
  name: string;
  required: boolean;
  productId: number;
  updateAddOnSelection: (
    addOnId: number,
    propertyId: string,
    value: string
  ) => void;
  userSelections: UserSelection[];
}>) {
  const currentValue =
    userSelections.find((selection) => selection.optionId === id.toString())
      ?.selectedValue || '';

  const handleChange = (value: string) => {
    updateAddOnSelection(productId, id.toString(), value);
  };

  return (
    <TextInput
      errorMessage="Please enter a value"
      hasError={hasError}
      id={id.toString()}
      label={name}
      maxLength={255}
      name={convertToKebabCase(name)}
      required={required}
      showCharacterCounter={false}
      size="medium"
      value={currentValue}
      onChange={(evt) => handleChange(evt.target.value)}
    />
  );
}

function AddOnQuantityPicker({
  addOnQuantity,
  productId,
  updateAddOnQuantity,
}: Readonly<{
  addOnQuantity: number;
  productId: number;
  updateAddOnQuantity: (quantity: number) => void;
}>) {
  const handleChange = (value: number) => {
    updateAddOnQuantity(value);
  };

  return (
    <NumberInput
      id={`${productId}-quantity`}
      label="Quantity"
      max={9}
      min={1}
      size="medium"
      value={addOnQuantity}
      width="50%"
      onChangeValue={handleChange}
    />
  );
}
